import { PanelLeft } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { LogoMark } from '@/components/Wordmark'
import type { Me } from '@/lib/api'
import type { Family } from '@/lib/families'
import { useFrame } from '@/lib/frame'
import { useRecent } from '@/lib/recent'
import { cn } from '@/lib/utils'
import { PhotosIcon, RecipesIcon } from './icons'
import { WaitingDot } from './WaitingDot'
import type { Place } from './places'
import { ProfileMenu } from './ProfileMenu'
import { YouFace } from './YouFace'

const ring = 'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50'
const roundButton = cn(
  'grid size-10 shrink-0 place-items-center rounded-full text-foreground transition-colors hover:bg-secondary',
  ring,
)

// The sidebar's own button, the first thing in the tab bar and at the top of the sidebar, as in Apple's iPad apps.
function SidebarButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={open ? 'Hide sidebar' : 'Show sidebar'}
      title={open ? 'Hide sidebar' : 'Show sidebar'}
      aria-expanded={open}
      className={roundButton}
    >
      <PanelLeft className="size-5 stroke-[1.75]" aria-hidden />
    </button>
  )
}

// A tablet's or computer's top bar, the way Apple's iPad apps lay theirs out: the GF mark at the left, the places as
// words in a floating pill in the middle with the sidebar button before them, and the page's "+" (CreateAction) and
// you at the right. Your picture wears the colour of the circle you're in and switches circles (ProfileMenu). With
// the sidebar open beside the page, the places are in it, so the bar keeps only its right end.
export function TopBar({
  me,
  places,
  sidebar,
  beside,
  onToggle,
}: {
  me: Me
  places: Place[]
  sidebar: boolean
  beside: boolean
  onToggle: () => void
}) {
  const { setActionSlot } = useFrame()
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  const tabs = places.length > 0 && !beside
  return (
    <header
      className={cn(
        'sticky top-0 z-20 -mb-8 grid h-19 grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-transparent bg-background/85 px-5 backdrop-blur-lg backdrop-saturate-150 transition-colors',
        scrolled && 'border-border',
      )}
    >
      <div className="flex min-w-0 items-center">
        {!beside && (
          <Link to={places[0]?.to ?? '/'} aria-label="gatheredfrom home" className={cn('rounded-xl p-1', ring)}>
            <LogoMark className="h-6" />
          </Link>
        )}
      </div>
      {tabs ? (
        <nav
          aria-label="Places"
          className="flex items-center gap-0.5 rounded-full border border-border/70 bg-background/80 p-1 shadow-float backdrop-blur-xl backdrop-saturate-150"
        >
          <SidebarButton open={sidebar} onToggle={onToggle} />
          <span aria-hidden="true" className="mx-1 h-5 w-px bg-border" />
          {places.map((place) => (
            <Link
              key={place.key}
              to={place.to}
              aria-current={place.active ? 'page' : undefined}
              className={cn(
                'flex h-10 items-center rounded-full px-4 text-[0.9375rem] font-medium text-muted-foreground transition-colors hover:text-foreground aria-[current=page]:bg-brand aria-[current=page]:text-brand-foreground',
                ring,
              )}
            >
              {place.label}
              {place.dot && <WaitingDot className="ml-1.5" />}
            </Link>
          ))}
        </nav>
      ) : (
        <span />
      )}
      <div className="flex items-center justify-end gap-1.5">
        <div ref={setActionSlot} className="flex items-center empty:hidden" />
        <ProfileMenu me={me} align="end">
          <button type="button" aria-label={`${me.name}, profile`} className={roundButton}>
            <YouFace me={me} className="size-8 text-xs" />
          </button>
        </ProfileMenu>
      </div>
    </header>
  )
}

const item = cn(
  'flex min-h-10 w-full min-w-0 items-center gap-3 rounded-xl px-2.5 text-left text-[0.9375rem] transition-colors hover:bg-secondary aria-[current]:bg-secondary aria-[current]:font-semibold',
  ring,
)

// What the sidebar holds: the places and the albums and books you opened last. You're at the foot, as before, and
// switch circles and spaces from there too.
function SidebarContent({
  me,
  family,
  places,
  onToggle,
  onGo,
}: {
  me: Me
  family: Family | null
  places: Place[]
  onToggle: () => void
  onGo: () => void
}) {
  const recent = useRecent(family?.id ?? null)
  return (
    <>
      <div className="flex items-center justify-between pb-2 pl-1.5">
        <Link
          to={places[0]?.to ?? '/'}
          onClick={onGo}
          aria-label="gatheredfrom home"
          className={cn('rounded-xl p-1', ring)}
        >
          <LogoMark className="h-6" />
        </Link>
        <SidebarButton open onToggle={onToggle} />
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
        <nav aria-label="Places" className="flex flex-col gap-0.5">
          {places.map((place) => (
            <Link
              key={place.key}
              to={place.to}
              onClick={onGo}
              aria-current={place.active ? 'page' : undefined}
              className={item}
            >
              <place.icon className="size-5 shrink-0" active={place.active} />
              <span className="truncate">{place.label}</span>
              {place.dot && <WaitingDot />}
            </Link>
          ))}
        </nav>
        {recent.length > 0 && (
          <nav aria-labelledby="sidebar-recent" className="flex flex-col gap-0.5">
            <h2
              id="sidebar-recent"
              className="px-2.5 pt-5 pb-1 text-xs tracking-[0.1em] text-muted-foreground uppercase"
            >
              Recent
            </h2>
            {recent.map((visit) => {
              const Icon = visit.kind === 'album' ? PhotosIcon : RecipesIcon
              return (
                <Link
                  key={visit.to}
                  to={visit.to}
                  onClick={onGo}
                  className={cn(item, 'text-muted-foreground hover:text-foreground')}
                >
                  <Icon className="size-5 shrink-0" />
                  <span className="truncate">{visit.title}</span>
                </Link>
              )
            })}
          </nav>
        )}
      </div>
      <div className="mt-2 border-t border-border/70 pt-2">
        <ProfileMenu me={me} side="top">
          <button type="button" className={item} aria-label={`${me.name}, profile`}>
            <YouFace me={me} className="size-6 text-xs" />
            <span className="min-w-0 truncate">{me.name}</span>
          </button>
        </ProfileMenu>
      </div>
    </>
  )
}

const panel =
  'fixed top-3 bottom-3 left-3 z-30 flex w-68 flex-col rounded-3xl border border-border/70 bg-background/90 p-3 shadow-float backdrop-blur-xl backdrop-saturate-150'

// The sidebar floats at the left as a frosted panel. On a wide screen it sits beside the page and stays open until
// you close it (Shell remembers); on an upright tablet it slides over the page and closes once you've picked
// somewhere to go.
export function Sidebar({
  me,
  family,
  places,
  open,
  beside,
  onToggle,
}: {
  me: Me
  family: Family | null
  places: Place[]
  open: boolean
  beside: boolean
  onToggle: () => void
}) {
  if (beside)
    return open ? (
      <aside aria-label="gatheredfrom" className={cn(panel, 'animate-in duration-200 fade-in-0 slide-in-from-left-4')}>
        <SidebarContent me={me} family={family} places={places} onToggle={onToggle} onGo={() => {}} />
      </aside>
    ) : null
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && onToggle()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-30 bg-black/25 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            panel,
            'z-40 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-left-4 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-left-4',
          )}
        >
          <DialogPrimitive.Title className="sr-only">gatheredfrom</DialogPrimitive.Title>
          <SidebarContent me={me} family={family} places={places} onToggle={onToggle} onGo={onToggle} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
